import { Controller } from "@hotwired/stimulus"

// Favoritos de itens do MenuHub, persistidos em localStorage (por navegador).
// - Botão estrela em cada card (target "toggle") adiciona/remove o item.
// - Na home, o target "grid" é preenchido via JS com os cards favoritados.
const STORE_KEY = "menuhub:favorites:v1"
const SEED_KEY = "menuhub:favorites:seeded:v1"

export default class extends Controller {
  static targets = ["toggle", "grid", "listGrid", "empty"]
  static values = { defaults: Array }

  connect() {
    this.seedDefaults()
    this.syncToggles()
    this.renderGrid()
  }

  // Semeia favoritos padrão na 1ª visita (ex.: Adesões, Pessoas, Dashboard).
  // Só roda uma vez; remoções do usuário persistem depois.
  seedDefaults() {
    const defaults = this.hasDefaultsValue ? this.defaultsValue : []
    if (!defaults.length) return
    if (localStorage.getItem(SEED_KEY)) return

    const map = this.load()
    defaults.forEach((it) => {
      if (it && it.key && !map[it.key]) map[it.key] = it
    })
    this.save(map)
    localStorage.setItem(SEED_KEY, "1")
  }

  // ---------- storage ----------
  load() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {}
    } catch (e) {
      return {}
    }
  }

  save(map) {
    localStorage.setItem(STORE_KEY, JSON.stringify(map))
  }

  // ---------- ações ----------
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const btn = event.currentTarget
    const key = btn.dataset.favKey
    if (!key) return

    const map = this.load()
    if (map[key]) {
      delete map[key]
    } else {
      map[key] = {
        key,
        title: btn.dataset.favTitle || "",
        desc: btn.dataset.favDesc || "",
        path: btn.dataset.favPath || "#",
        icon: btn.dataset.favIcon || "📌"
      }
    }
    this.save(map)
    this.syncToggles()
    this.renderGrid()
  }

  // ---------- sincronização visual ----------
  syncToggles() {
    const map = this.load()
    this.toggleTargets.forEach((btn) => {
      const active = Boolean(map[btn.dataset.favKey])
      btn.classList.toggle("is-favorite", active)
      btn.setAttribute("aria-pressed", active ? "true" : "false")
      btn.title = active ? "Remover dos favoritos" : "Adicionar aos favoritos"
      const icon = btn.querySelector("i")
      if (icon) icon.className = active ? "fas fa-star" : "far fa-star"
    })
  }

  renderGrid() {
    const map = this.load()
    const all = Object.values(map)

    if (this.hasGridTarget) {
      this.pruneNonFavorites(this.gridTarget, map)
      this.injectFavorites(this.gridTarget, all, (it) => this.cardHTML(it))
    }
    if (this.hasListGridTarget) {
      this.pruneNonFavorites(this.listGridTarget, map)
      this.injectFavorites(this.listGridTarget, all, (it) => this.listRowHTML(it))
    }

    if (this.hasEmptyTarget) {
      const visible = this.hasGridTarget
        ? this.gridTarget.querySelectorAll("[data-menu-hub-item]").length
        : 0
      this.emptyTarget.classList.toggle("d-none", visible > 0)
    }
  }

  // Remove do grid do home os itens (inclusive os do servidor) que não estão
  // mais favoritados — desfavoritar tira do home. Só atua onde há grid target.
  pruneNonFavorites(container, map) {
    container.querySelectorAll("[data-menu-hub-item]").forEach((el) => {
      const btn = el.querySelector("[data-fav-key]")
      if (btn && !map[btn.dataset.favKey]) el.remove()
    })
  }

  // Remove favoritos antigos, mantém itens do servidor, anexa favoritos
  // que ainda não estão presentes (dedupe por href). Retorna nº anexados.
  injectFavorites(container, all, builder) {
    container.querySelectorAll("[data-fav-card]").forEach((el) => el.remove())

    const existing = new Set(
      Array.from(container.querySelectorAll("a[href]")).map((a) =>
        a.getAttribute("href")
      )
    )
    const items = all.filter((it) => !existing.has(it.path))
    container.insertAdjacentHTML("beforeend", items.map(builder).join(""))
    return items.length
  }

  cardHTML(it) {
    const e = (s) =>
      String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
      )

    return `
      <li class="col position-relative" style="list-style: none;"
          data-fav-card="true"
          data-menu-hub-item="true"
          data-title="${e(it.title).toLowerCase()}"
          data-desc="${e(it.desc).toLowerCase()}">
        <button type="button" class="menuhub-fav-btn is-favorite"
                data-favorites-target="toggle"
                data-action="click->favorites#toggle"
                data-fav-key="${e(it.key)}"
                data-fav-title="${e(it.title)}"
                data-fav-desc="${e(it.desc)}"
                data-fav-path="${e(it.path)}"
                data-fav-icon="${e(it.icon)}"
                aria-pressed="true" title="Remover dos favoritos">
          <i class="fas fa-star"></i>
        </button>
        <a href="${e(it.path)}" class="card h-100 text-decoration-none transition-base bg-body border-subtle shadow-sm hover-shadow">
          <div class="card-body d-flex align-items-center gap-3 p-2 p-md-3">
            <span class="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 bg-body-secondary" style="width: 2.25rem; height: 2.25rem;">${e(it.icon)}</span>
            <div class="text-truncate">
              <div class="d-flex align-items-center gap-2">
                <div class="fs-6 fw-medium text-truncate nav-link-text text-body">${e(it.title)}</div>
              </div>
              <div class="small text-body-secondary text-truncate">${e(it.desc)}</div>
            </div>
          </div>
        </a>
      </li>`
  }

  // Linha de favorito na visão Lista (espelha o markup do server).
  listRowHTML(it) {
    const e = (s) =>
      String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
      )

    return `
      <div class="d-flex align-items-center border-bottom bg-body"
           data-fav-card="true"
           data-menu-hub-item="true"
           data-title="${e(it.title).toLowerCase()}"
           data-desc="${e(it.desc).toLowerCase()}">
        <a href="${e(it.path)}" class="list-group-item list-group-item-action border-0 d-flex align-items-center justify-content-between p-3 flex-grow-1 bg-transparent">
          <div class="d-flex align-items-center gap-3 text-truncate">
            <span class="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 bg-body-tertiary" style="width: 2.25rem; height: 2.25rem;">${e(it.icon)}</span>
            <div class="text-truncate">
              <div class="fs-6 fw-medium text-truncate">${e(it.title)}</div>
              <div class="small text-secondary text-truncate">${e(it.desc)}</div>
            </div>
          </div>
          <span class="text-secondary">&rarr;</span>
        </a>
        <button type="button" class="menuhub-fav-btn menuhub-fav-btn--list is-favorite"
                data-favorites-target="toggle"
                data-action="click->favorites#toggle"
                data-fav-key="${e(it.key)}"
                data-fav-title="${e(it.title)}"
                data-fav-desc="${e(it.desc)}"
                data-fav-path="${e(it.path)}"
                data-fav-icon="${e(it.icon)}"
                aria-pressed="true" title="Remover dos favoritos">
          <i class="fas fa-star"></i>
        </button>
      </div>`
  }
}